import React from "react";
import {
    FiX, FiUser, FiClock, FiCalendar, FiTag, FiBan,
    FiTrash2, FiAlertTriangle, FiCheck, FiPhone, FiInfo
} from "react-icons/fi";
import {BsBan} from "react-icons/bs";

const addMinutesToTime = (timeStr, minutesToAdd) => {
    if (!timeStr) return "";
    const [hours, minutes] = timeStr.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    date.setMinutes(date.getMinutes() + minutesToAdd);
    return `${String(date.getHours()).padStart(2, "0")}:${String(date.getMinutes()).padStart(2, "0")}`;
};

const getStatusLabel = (status) => {
    switch (status) {
        case "pending": return { label: "Beklemede", color: "#f59f00", bg: "rgba(245,159,0,0.12)" };
        case "approved": return { label: "Onaylandı", color: "#10b981", bg: "rgba(16,185,129,0.12)" };
        case "completed": return { label: "Tamamlandı", color: "#0ea5e9", bg: "rgba(14,165,233,0.12)" };
        case "cancelled": return { label: "İptal", color: "#ef4444", bg: "rgba(239,68,68,0.12)" };
        case "no_show": return { label: "Gelmedi", color: "#f97316", bg: "rgba(249,115,22,0.12)" };
        default: return { label: status, color: "#94a3b8", bg: "rgba(148,163,184,0.12)" };
    }
};

const AppointmentDetailModal = ({
                                    appt,
                                    onClose,
                                    onCancel,
                                    onNoShow,
                                    onAddToBlacklist,
                                }) => {
    if (!appt) return null;

    let totalDuration = 0;
    if (appt.appointment_duration) {
        totalDuration = parseInt(appt.appointment_duration);
    } else {
        const sDur = parseInt(appt.service?.duration || 30);
        const o1Dur = appt.optionalService1 ? parseInt(appt.optionalService1.duration || 0) : 0;
        const o2Dur = appt.optionalService2 ? parseInt(appt.optionalService2.duration || 0) : 0;
        totalDuration = sDur + o1Dur + o2Dur;
    }

    const mainServiceName = appt.service_name || appt.service?.name;
    const opt1Name = appt.optional_service_name_1 || appt.optionalService1?.name;
    const opt2Name = appt.optional_service_name_2 || appt.optionalService2?.name;
    const endTime = addMinutesToTime(appt.appointment_time?.substring(0, 5), totalDuration);
    const statusInfo = getStatusLabel(appt.status);

    const formatDate = (d) => {
        if (!d) return "-";
        return new Date(d).toLocaleDateString("tr-TR", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
    };

    return (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-navy-900/70 backdrop-blur-md">
            <div
                className="bg-white dark:bg-navy-800 rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden"
                style={{ animation: "scaleIn 0.2s ease-out" }}
            >
                {/* Header */}
                <div
                    className="p-5 flex justify-between items-start"
                    style={{ borderBottom: "1px solid var(--border-color, rgba(99,115,129,0.15))" }}
                >
                    <div>
                        <h2 className="text-xl font-bold text-navy-700 dark:text-white flex items-center gap-2">
                            <FiCalendar className="text-brand-500" />
                            Randevu Detayı
                        </h2>
                        <span
                            className="inline-flex items-center gap-1.5 mt-2 text-xs font-bold px-3 py-1 rounded-full"
                            style={{ color: statusInfo.color, backgroundColor: statusInfo.bg }}
                        >
              <FiCheck size={12} />
                            {statusInfo.label}
            </span>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-xl text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                    >
                        <FiX size={22} />
                    </button>
                </div>

                {/* Body */}
                <div className="p-5 space-y-4">
                    {/* Müşteri */}
                    <div className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-navy-900 rounded-2xl">
                        <div className="w-12 h-12 rounded-2xl bg-brand-500 flex items-center justify-center text-white font-bold text-lg">
                            {((appt.customer?.name || "?")[0] + (appt.customer?.surname || "")[0]).toUpperCase()}
                        </div>
                        <div>
                            <div className="font-bold text-navy-700 dark:text-white text-lg">
                                {appt.customer?.name} {appt.customer?.surname}
                            </div>
                            {appt.customer?.phone && (
                                <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1 mt-0.5">
                                    <FiPhone size={12} /> {appt.customer.phone}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Hizmet */}
                    <div className="p-4 bg-gray-50 dark:bg-navy-900 rounded-2xl space-y-2">
                        <div className="text-xs font-bold text-gray-400 uppercase tracking-wide">Hizmet</div>
                        <div className="font-bold text-navy-700 dark:text-white text-base">
                            {mainServiceName}
                            {opt1Name && <span className="text-brand-500 font-semibold"> + {opt1Name}</span>}
                            {opt2Name && <span className="text-brand-500 font-semibold"> + {opt2Name}</span>}
                        </div>
                        <div className="flex flex-wrap gap-3 mt-2 text-sm text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1.5">
                <FiCalendar size={13} className="text-brand-500" />
                  {formatDate(appt.appointment_date)}
              </span>
                            <span className="flex items-center gap-1.5">
                <FiClock size={13} className="text-brand-500" />
                                {appt.appointment_time?.substring(0, 5)} - {endTime}
                                <span className="text-xs text-gray-400">({totalDuration} dk)</span>
              </span>
                        </div>
                    </div>

                    {/* Personel */}
                    <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 dark:bg-navy-900 rounded-2xl">
                        <FiUser className="text-brand-500" size={16} />
                        <div>
                            <div className="text-xs text-gray-400 font-bold uppercase tracking-wide">Personel</div>
                            <div className="font-semibold text-navy-700 dark:text-white text-sm">
                                {appt.staff?.name} {appt.staff?.surname}
                            </div>
                        </div>
                        {appt.price && (
                            <div className="ml-auto text-right">
                                <div className="text-xs text-gray-400 font-bold uppercase tracking-wide">Tutar</div>
                                <div className="font-bold text-brand-500">{parseFloat(appt.price).toFixed(2)} ₺</div>
                            </div>
                        )}
                    </div>

                    {/* Not */}
                    {appt.note && (
                        <div className="flex items-start gap-3 px-4 py-3 bg-amber-50 dark:bg-amber-900/10 rounded-2xl border border-amber-100 dark:border-amber-800/30">
                            <FiInfo className="text-amber-500 mt-0.5 shrink-0" size={15} />
                            <p className="text-sm text-amber-700 dark:text-amber-300 italic">{appt.note}</p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div
                    className="p-4 flex justify-between items-center"
                    style={{ borderTop: "1px solid var(--border-color, rgba(99,115,129,0.15))", background: "var(--footer-bg)" }}
                >
                    <div className="flex gap-2">
                        {appt.status === "no_show" ? (
                            <button
                                onClick={onAddToBlacklist}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-red-500 border border-red-200 dark:border-red-800 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all"
                            >
                                <BsBan size={15} /> Kara Listeye Al
                            </button>
                        ) : (
                            <button
                                onClick={onNoShow}
                                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-amber-500 border border-amber-200 dark:border-amber-800 hover:bg-amber-50 dark:hover:bg-amber-900/20 transition-all"
                            >
                                <FiAlertTriangle size={15} /> Gelmedi
                            </button>
                        )}
                    </div>
                    <button
                        onClick={onCancel}
                        className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-bold text-white bg-red-500 hover:bg-red-600 shadow-lg shadow-red-500/20 transition-all"
                    >
                        <FiTrash2 size={15} /> İptal Et
                    </button>
                </div>
            </div>
        </div>
    );
};

export default AppointmentDetailModal;